import { useEffect, useRef, type MutableRefObject, type RefObject } from 'react';

export type ImpactMotionRefs = {
  cardRef: RefObject<HTMLDivElement>;
  parallaxRef: RefObject<HTMLDivElement>;
  warpRef: RefObject<HTMLDivElement>;
  radarRef: RefObject<HTMLDivElement>;
  counterSpinRefs: MutableRefObject<Array<HTMLDivElement | null>>;
};

const LINEAR_SPIN = { duration: 120000, iterations: Infinity, easing: 'linear' } as const;
const PARALLAX_TRANSITION = 'transform 900ms cubic-bezier(0.165, 0.84, 0.44, 1)';
const PARALLAX_RANGE_PX = 14;
const HOVER_SCALE = 1.06;
const POINTER_SCALE_GAIN = 0.06;
const WARP_EXPAND = { duration: 9000, easing: 'ease-out', fill: 'forwards', iterations: 1 } as const;

function spin(node: Element | null, reverse = false) {
  if (!node) {
    return null;
  }

  return node.animate(
    [{ transform: 'rotate(0deg)' }, { transform: reverse ? 'rotate(-360deg)' : 'rotate(360deg)' }],
    LINEAR_SPIN
  );
}

function pointerOffset(event: PointerEvent, card: HTMLElement) {
  const rect = card.getBoundingClientRect();

  return {
    x: (event.clientX - rect.left) / rect.width - 0.5,
    y: (event.clientY - rect.top) / rect.height - 0.5
  };
}

function applyParallax(node: HTMLElement, x: number, y: number, scale: number) {
  node.style.transform = `translate(${x * PARALLAX_RANGE_PX}px, ${y * PARALLAX_RANGE_PX}px) scale(${scale})`;
  node.style.transition = PARALLAX_TRANSITION;
}

function pushOffset(current: number, next: number) {
  if (Math.abs(next) <= Math.abs(current)) {
    return current;
  }

  if (current !== 0 && Math.sign(next) !== Math.sign(current)) {
    return current;
  }

  return next;
}

function playIfPending(animation: Animation) {
  if (animation.playState === 'finished') {
    return;
  }

  animation.play();
}

function clearParallax(node: HTMLElement) {
  node.style.removeProperty('transform');
  node.style.removeProperty('transition');
  node.style.removeProperty('will-change');
}

function startCardMotion({
  card,
  parallax,
  warp,
  radar,
  counterNodes
}: {
  card: HTMLDivElement;
  parallax: HTMLDivElement;
  warp: HTMLDivElement | null;
  radar: HTMLDivElement | null;
  counterNodes: Array<HTMLDivElement | null>;
}) {
  let frame = 0;
  let latestPosition = { x: 0, y: 0 };
  let scale = 1;
  let inView = false;
  let active = false;

  const growScale = (x: number) => {
    const pointerScale = HOVER_SCALE + Math.abs(x) * POINTER_SCALE_GAIN;
    scale = Math.max(scale, pointerScale);
  };

  const warpAnimation =
    warp?.animate([{ transform: 'rotate(0deg) scale(1)' }, { transform: 'rotate(1.4deg) scale(1.03)' }], WARP_EXPAND) ??
    null;

  const animations = [warpAnimation, spin(radar), ...counterNodes.map(node => spin(node, true))].filter(
    Boolean
  ) as Animation[];

  animations.forEach(animation => animation.pause());

  const renderParallax = () => {
    frame = 0;

    if (!active) {
      return;
    }

    applyParallax(parallax, latestPosition.x, latestPosition.y, scale);
  };

  const scheduleParallax = () => {
    if (!active || frame) {
      return;
    }

    frame = window.requestAnimationFrame(renderParallax);
  };

  const expandFromPointer = (position: { x: number; y: number }) => {
    const previousScale = scale;
    const previousPosition = latestPosition;

    growScale(position.x);
    latestPosition = {
      x: pushOffset(latestPosition.x, position.x),
      y: pushOffset(latestPosition.y, position.y)
    };

    if (scale === previousScale && latestPosition.x === previousPosition.x && latestPosition.y === previousPosition.y) {
      return;
    }

    scheduleParallax();
  };

  const setActive = (next: boolean) => {
    if (active === next) {
      return;
    }

    active = next;

    if (next) {
      animations.forEach(playIfPending);
      scheduleParallax();
      return;
    }

    animations.forEach(animation => animation.pause());

    if (frame) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }
  };

  const syncActive = () => {
    setActive(inView && !document.hidden);
  };

  const onPointerEnter = () => {
    if (!active) {
      return;
    }

    parallax.style.willChange = 'transform';
    expandFromPointer(latestPosition);
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!active) {
      return;
    }

    expandFromPointer(pointerOffset(event, card));
  };

  const onPointerLeave = () => {
    parallax.style.removeProperty('will-change');
  };

  const onVisibilityChange = () => {
    syncActive();
  };

  const observer = new IntersectionObserver(
    ([entry]) => {
      inView = Boolean(entry?.isIntersecting);
      syncActive();
    },
    { threshold: 0 }
  );

  card.addEventListener('pointerenter', onPointerEnter);
  card.addEventListener('pointermove', onPointerMove);
  card.addEventListener('pointerleave', onPointerLeave);
  document.addEventListener('visibilitychange', onVisibilityChange);
  observer.observe(card);
  syncActive();

  return () => {
    card.removeEventListener('pointerenter', onPointerEnter);
    card.removeEventListener('pointermove', onPointerMove);
    card.removeEventListener('pointerleave', onPointerLeave);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    observer.disconnect();

    if (frame) {
      window.cancelAnimationFrame(frame);
    }

    clearParallax(parallax);
    animations.forEach(animation => animation.cancel());
  };
}

export function useImpactMotion(enableRadar = false): ImpactMotionRefs {
  const cardRef = useRef<HTMLDivElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const warpRef = useRef<HTMLDivElement>(null);
  const radarRef = useRef<HTMLDivElement>(null);
  const counterSpinRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    let stopMotion: (() => void) | undefined;

    const syncMotionPreference = () => {
      stopMotion?.();

      const card = cardRef.current;
      const parallax = parallaxRef.current;

      if (media.matches || !card || !parallax) {
        stopMotion = undefined;
        return;
      }

      stopMotion = startCardMotion({
        card,
        parallax,
        warp: warpRef.current,
        radar: enableRadar ? radarRef.current : null,
        counterNodes: enableRadar ? counterSpinRefs.current : []
      });
    };

    syncMotionPreference();
    media.addEventListener('change', syncMotionPreference);

    return () => {
      media.removeEventListener('change', syncMotionPreference);
      stopMotion?.();
    };
  }, [enableRadar]);

  return { cardRef, parallaxRef, warpRef, radarRef, counterSpinRefs };
}
